import { createContext, ReactNode, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingPage } from "~/components/loading";
import { Org, SpaceData } from "~/lib/types";
import { isUnauthRoute } from "~/unauthenticatedApp/unauthenticatedApp";
import { Config } from "~/constants/configuration_provider";
import {
  LoadOrganizationDocument,
  LoadSpaceDocument,
  LoadViewerDocument,
  LoadViewerQuery,
  useDeleteOrganizationMutation,
  useDeleteSpaceMutation,
  useLoadViewerQuery,
  UserState,
  useSetViewerSettingMutation,
  useUpdateOrganizationNameMutation,
  useUpdateSpaceMutation,
} from "~/operations";
import { parseJwt } from "~/lib/parseJwt";
import { useAuth } from "~/login/provider";
import { datadogRum } from "@datadog/browser-rum";
import { ColorModeSetting } from "./color-mode";
import { Metrics } from "~/lib/arrow";

export type Region = {
  name: string;
  url: string;
};

export type Viewer = NonNullable<LoadViewerQuery["viewer"]>;

export type RawViewerSettings = LoadViewerQuery["viewerSettings"];

export type ViewerSettings = {
  last_space_id?: string;
  colorblind?: string;
  colormode?: ColorModeSetting;
  assetDoNotAskToDelete?: "true" | "false";
};

export type ViewerContextTypes =
  | {
      viewer: Viewer | undefined;
      viewerSettings: ViewerSettings | undefined;
      refetch: ReturnType<typeof useLoadViewerQuery>["refetch"];
      firstSpace: () => SpaceData | undefined;
      findOrg: (orgId: string) => Org | null | undefined;
      currentOrg: () => Org | null | undefined;
      updateOrgName: ReturnType<typeof useUpdateOrganizationNameMutation>[0];
      updateSpace: ReturnType<typeof useUpdateSpaceMutation>[0];
      deleteOrg: ReturnType<typeof useDeleteOrganizationMutation>[0];
      deleteSpace: ReturnType<typeof useDeleteSpaceMutation>[0];
      updateViewerSettings: ReturnType<typeof useSetViewerSettingMutation>[0];
    }
  | undefined;

export const ViewerContext = createContext<ViewerContextTypes>(undefined);

export type ViewerProviderProps = { children: ReactNode };

export function ViewerProvider({ children }: ViewerProviderProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  // APOLLO
  const { loading, data, refetch } = useLoadViewerQuery({
    errorPolicy: "all",
    variables: {
      metricMrns: [Metrics.AssetsRiskRatingDistribution],
    },
  });

  const viewer = data?.viewer ? data.viewer : undefined;
  const viewerSettings = data?.viewerSettings
    ? buildViewerSettings(data.viewerSettings)
    : undefined;

  useEffect(() => {
    if (viewer && viewerSettings) {
      // load up the viewer and viewerSettings data into context
      loadViewer(viewer, viewerSettings);

      if (Config.VITE_DATADOG_ENABLE === "true") {
        const email = viewer.email ?? undefined;
        const isMondooEmployee = ["mondoo.com", "mondoo.io"].some((domain) =>
          email?.endsWith(domain),
        );
        datadogRum.setUser({
          id: viewer.mrn,
          name: viewer.name,
          email,
          isMondooEmployee,
        });
      }
    }
    // we run these functions again only if Viewer data changes.
  }, [viewer, viewerSettings]);

  const loadViewer = async (viewer: Viewer, settings: ViewerSettings) => {
    // Navigate to last space used if loading into
    // top-level dashboard and lastSpaceId is set
    const lastSpaceId = settings.last_space_id;

    const isRootPath = window.location.pathname === "/";
    const isRedirectPath =
      isRootPath || isUnauthRoute(window.location.pathname);

    const isFirebaseAuth = Config.VITE_AUTH_PROVIDER === "firebase";
    const viewerIsEnabled = viewer.state === UserState.Enabled;
    const viewerIsSsoUser = await (async () => {
      if (!isFirebaseAuth || !auth.state.user) return false;
      const idToken = await auth.state.user.getIdToken();
      const token = parseJwt(idToken);
      const signinprovider = token?.firebase?.sign_in_provider as string;
      return signinprovider.startsWith("saml");
    })();

    const shouldRedirect =
      isRedirectPath && (viewerIsEnabled || viewerIsSsoUser);

    if (shouldRedirect) {
      if (lastSpaceId) {
        // Redirect to dashboard for last visited space
        navigate(`/space/overview?spaceId=${lastSpaceId}`);
      } else {
        navigate("/dashboard");
      }
    }
  };

  // Find a single organization using the the organization ID
  const findOrg = (orgId?: string | null): Org | null | undefined => {
    if (!viewer) return null;
    const orgs = viewer.organizations;
    if (!orgs) return null;
    return orgs.find((x: Org) => x.id === orgId);
  };

  const currentOrg = (): Org | null | undefined => {
    const queryParams = new URLSearchParams(window.location.search);

    // extract org information
    const orgId = queryParams.get("organizationId");
    return findOrg(orgId);
  };

  // LoadFirstSpace
  // useLoadFirstSpace
  const firstSpace = (): SpaceData | undefined => {
    return viewer?.firstSpace || undefined;
  };

  //// APOLLO MUTATIONS
  const [updateViewerSettings] = useSetViewerSettingMutation();

  // Update an organization's display name
  const [updateOrgName] = useUpdateOrganizationNameMutation();

  // Delete an organization
  const [deleteOrg] = useDeleteOrganizationMutation({
    refetchQueries: [LoadViewerDocument, LoadOrganizationDocument],
  });

  // Update a space's display name
  const [updateSpace] = useUpdateSpaceMutation({
    refetchQueries: [LoadViewerDocument, LoadSpaceDocument],
  });

  // Delete a space from within an organization
  const [deleteSpace] = useDeleteSpaceMutation({
    refetchQueries: [LoadViewerDocument, LoadSpaceDocument],
  });

  if (loading || !viewer) {
    return <LoadingPage />;
  }

  return (
    <ViewerContext.Provider
      value={{
        viewer,
        viewerSettings,
        findOrg,
        currentOrg,
        refetch,
        firstSpace,
        updateOrgName,
        updateSpace,
        updateViewerSettings,
        deleteOrg,
        deleteSpace,
      }}
    >
      {viewer && children}
    </ViewerContext.Provider>
  );
}

export function useViewer() {
  const context = useContext(ViewerContext);
  if (context === undefined) {
    throw new Error("useViewer must be used within a ViewerProvider");
  }
  return context;
}

const buildViewerSettings = (
  rawSettings: RawViewerSettings,
): ViewerSettings => {
  const settings: ViewerSettings = {};
  rawSettings.forEach(({ key, value }) => {
    if (
      key === "assetDoNotAskToDelete" &&
      (value === "true" || value === "false")
    ) {
      settings.assetDoNotAskToDelete = value;
    }

    if (key === "colorblind") {
      settings.colorblind = value;
    }

    if (
      key === "colormode" &&
      (value === "light" || value === "dark" || value === "system")
    ) {
      settings.colormode = value as ColorModeSetting;
    }

    if (key === "last_space_id") {
      settings.last_space_id = value;
    }
  });
  return settings;
};
